import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'
import {
  usePageDesignerEditMode,
  useConfig,
  Region,
  transformRegionToMap
} from 'msp-integrations'
import { NewsletterModalProps } from './NewsletterModal.types'
import NewsletterForm from '@molecules/NewsletterForm/NewsletterForm'
import Modal from '@molecules/Modal/Modal'

const NewsletterModal = ({ data, regions }: NewsletterModalProps) => {
  const { formatMessage } = useIntl()
  const pageDesignerEditMode = usePageDesignerEditMode()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalWillOpen, setModalWillOpen] = useState(false)
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false)
  const { hash, pathname } = useLocation()
  const history = useHistory()
  const {
    cookiePrefix,
    theme: {
      font,
      mspComponents: { newsletterModal }
    }
  } = useConfig()

  const regionContent = transformRegionToMap(regions).get('content')

  const cookieName = cookiePrefix + 'Newsletter'

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    history.push(pathname)
  }

  const closeButtonClick = () => {
    setCookie(false)
    closeModal()
  }

  const subscriptionSuccess = () => {
    setCookie(true)
    setShowConfirmationMessage(true)
  }

  const modalHeader = (
    <div
      className={`${font.wide} text-12 ${newsletterModal.title} mb-5 uppercase`}
    >
      {data?.title}
    </div>
  )

  const confirmationMessage = (
    <div className={`${font.regular} text-16 ${newsletterModal.success}`}>
      {formatMessage({
        defaultMessage:
          'Thank you for your registration. You will soon receive an email to confirm your email address.',
        id: 'msp.newsletter.success_message'
      })}
    </div>
  )

  const setCookie = (value: boolean) => {
    if (!Cookies.get(cookieName)) {
      const oneYearAhead = new Date()
      oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1)
      Cookies.set(cookieName, value, { expires: oneYearAhead })
    }
  }

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCookie(false)
        closeModal()
      }
    }

    window.addEventListener('keydown', close)

    return () => {
      window.removeEventListener('keydown', close)
    }
  }, [])

  useEffect(() => {
    let timer: number
    const isDelayNumeric = /^\d+$/.test(data?.openDelay)
    if (hash == '#newsletter') {
      openModal()
      setModalWillOpen(true)
    } else if (isDelayNumeric && !hash) {
      const delay = parseInt(data.openDelay)
      timer = window.setTimeout(() => {
        openModal()
      }, delay)
      setModalWillOpen(true)
    }

    if (!hash && Cookies.get(cookieName)) {
      setModalWillOpen(false)
    }

    if (pageDesignerEditMode) {
      setModalWillOpen(false)
    }

    if (Cookies.get('appMode') && Cookies.get('appMode') === '1') {
      setModalWillOpen(false)
    }

    return () => {
      timer && clearTimeout(timer)
    }
  }, [hash])

  if (modalWillOpen) {
    return (
      <Modal
        style={`max-w-xl`}
        isOpen={isModalOpen}
        onClose={closeModal}
        onCloseButtonClick={closeButtonClick}
        overlay
        closeOnOverlayClick
        blockScroll
        modalHeader={modalHeader}
        modalBody={
          showConfirmationMessage ? (
            confirmationMessage
          ) : (
            <div className='h-full scroll-py-4 overflow-y-scroll'>
              <Region region={regionContent} className='mb-4' />
              <NewsletterForm
                id={`newsletter-form-modal_${data?.title || ''}`}
                onSuccess={subscriptionSuccess}
                noDescription={!!regionContent?.components}
                newsletterSource='modal'
              />
            </div>
          )
        }
      />
    )
  } else {
    return null
  }
}

export default NewsletterModal
